import api from './api';

interface RefinementCallbacks {
  onChunk: (chunk: string) => void;
  onComplete: (finalResponse: string) => void;
  onError: (error: Error) => void;
}

export const refineResponse = async (
  question: string,
  currentResponse: string,
  userPrompt: string,
  callbacks: RefinementCallbacks
): Promise<void> => {

  try {
    // Use backend API endpoint instead of direct OpenAI calls
    const response = await api.post('/ai/refine-response', {
      question,
      currentResponse,
      userPrompt
    });

    const refinedResponse = response.data.refinedResponse;
    
    // Simulate streaming by sending chunks (for smooth UI experience)
    const words = refinedResponse.split(' ');

    for (let i = 0; i < words.length; i++) {
      callbacks.onChunk(words[i] + ' ');
      
      // Small delay to simulate streaming
      if (i % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    callbacks.onComplete(refinedResponse);
    
  } catch (error) {
    console.error('Refinement error:', error);
    
    // Fallback refinement if backend fails
    const fallbackRefinement = generateFallbackRefinement(currentResponse, userPrompt);
    callbacks.onComplete(fallbackRefinement);
  }
};

const generateFallbackRefinement = (currentResponse: string, userPrompt: string): string => {
  // Simple fallback refinement logic
  const lowerPrompt = userPrompt.toLowerCase();
  let refinedResponse = currentResponse;

  if (lowerPrompt.includes('more specific') || lowerPrompt.includes('add details')) {
    refinedResponse = refinedResponse.replace(
      /\. /g, 
      '. Our team brings extensive experience in this area, with proven methodologies and industry-leading practices. '
    );
  }

  if (lowerPrompt.includes('confident') || lowerPrompt.includes('stronger')) {
    refinedResponse = refinedResponse.replace(
      /we can/gi, 
      'we will'
    ).replace(
      /may/gi, 
      'will'
    );
  }

  if (lowerPrompt.includes('technical') || lowerPrompt.includes('methodology')) {
    refinedResponse += '\n\nOur technical approach incorporates industry best practices and proven methodologies to ensure optimal results and compliance with all requirements.';
  }

  if (lowerPrompt.includes('experience') || lowerPrompt.includes('examples')) {
    refinedResponse += '\n\nOur organisation has successfully delivered similar projects, demonstrating our capability and commitment to excellence in this domain.';
  }

  return refinedResponse || currentResponse + '\n\n[Note: Response enhanced based on your feedback to improve clarity and impact.]';
};